import { RevxJobSize } from 'components/RevxJobSize';
import './WorkspaceItem.less';
import { useTranslation } from 'react-i18next';
import { useRef, useState, useMemo } from 'react';
import {
  FilterTabEnum,
  JobItem,
  JobWorkflowStatusEnum,
} from 'domains/workspaceDomain';
import { useOrg, useUser } from 'hooks';
import { useIsJobDetail } from '../hooks';
import { useLatest, useSize } from 'ahooks';
import useScrollDetailTab from '../hooks/useScrollDetailTab';
import RevxFlexLayout from 'components/RevxFlexLayout';
import RevxJobImg from 'components/RevxJobImg';
import { SectionHeading, Text, TextHeavy } from 'components/RevxTypography';
import { JobNum } from '../components/JobNum';
import { Divider, Tooltip } from 'antd';
import { ClockCircleOutlined, ShopOutlined } from '@ant-design/icons';
import { JOB_NAME_MAX_LENGTH } from 'utils/constants';
import { EditIcon } from 'components/Icons';
import { RevxMoney } from 'components/RevxMoney';
import CardInfoContacts from './components/CardInfoContacts';
import { WorkspaceFilterTabs } from './WorkspaceFilterTabs';
import { WorkspaceItemDetailWrapper } from '../components/WorkspaceItemDetailWrapper';
import CardInfoPDFs from './components/CardInfoPDFs';
import ContactLink from '../components/ContactLink';
import CardActions from './components/CardAcions';
import useJobCardInfo from '../hooks/useJobCardInfo';
import cs from 'classnames';
import useShowBillingContactFlag from '../hooks/useShowBillingContactFlag';
import { OrgTypeEnum } from 'features/Platform/components/OrgAdmin/OrgTypeSelectModal';
import { SiteRoleEnum } from 'domains/roleDomain';

type Props = {
  data: JobItem;
  onUpdate?: (data: JobItem) => void;
};

const UniversalWorkspaceItem = ({ data, onUpdate }: Props) => {
  const [activeTab, setActiveTab] = useState<FilterTabEnum | null>(null);
  const [scrollLoading, setScrollLoading] = useState<boolean>(false);

  const { t } = useTranslation();

  const user = useUser();
  const org = useOrg();
  const isJobDetail = useIsJobDetail();
  const cardInfo = useJobCardInfo(data);
  const showBillingContact = useShowBillingContactFlag();
  const ref = useRef(null);
  const size = useSize(ref);
  const latestCountRef = useLatest(size);
  const scrollDetailTab = useScrollDetailTab({
    setScrollLoading,
    latestCountRef,
  });

  // 判断是否是 Customer 组织的 Job，并获取组织名称
  const { isCustomerJob, jobOrgName } = useMemo(() => {
    if (org?.orgType !== OrgTypeEnum.SAAS) {
      return { isCustomerJob: false, jobOrgName: '' };
    }
    if (org?.id !== data.customerOrgId && org?.id !== data.supplierOrgId) {
      // 优先显示 customerOrgName，如果没有则显示 supplierOrgName
      console.log(
        'jobId : ',
        data.id,
        ' orgName:',
        data.customerOrgName,
        data.supplierOrgName
      );
      const orgName = data.customerOrgName || data.supplierOrgName || '';
      return { isCustomerJob: true, jobOrgName: orgName };
    }
    return { isCustomerJob: false, jobOrgName: '' };
  }, [
    org,
    data.customerOrgId,
    data.supplierOrgId,
    data.customerOrgName,
    data.supplierOrgName,
    data.id,
  ]);

  const {
    hasProducer,
    hasProxy,
    hasOperator,
    hasCustomer,
    isInternalJob,
    adjustmentJobAccepted,
  } = cardInfo;

  const priceNode = () => {
    let priceName = adjustmentJobAccepted ? 'Updated Price' : 'Price';
    let poAmount = adjustmentJobAccepted ? 'Updated PO Amount' : 'PO Amount';
    const buyerPrice = adjustmentJobAccepted
      ? 'Updated Buyer Price'
      : 'Buyer Price';
    if (hasOperator) {
      priceName = buyerPrice;
    }

    const tax = data.tax || 0;

    return (
      <>
        {(!hasProducer || (hasProducer && hasProxy)) && (
          <>
            <TextHeavy className="workspace-item-content-row-title">
              {priceName}:
            </TextHeavy>
            <Text className="workspace-item-content-row-t">
              <RevxMoney value={data.jobCharge + tax} />
            </Text>
            {(hasOperator || hasProducer) && <Divider type="vertical" />}
          </>
        )}
        {(hasOperator || hasProducer) && (
          <>
            <TextHeavy className="workspace-item-content-row-title">
              {poAmount}:
            </TextHeavy>
            <Text className="workspace-item-content-row-t">
              <RevxMoney value={data.supplierCharge} />
            </Text>
          </>
        )}
      </>
    );
  };

  const buyerInfoNode = () => {
    const producerInternalJob = isInternalJob && hasProducer;
    let n = (
      <>
        <div>
          <Text className="workspace-item-content-row-t">
            {producerInternalJob ? cardInfo.billingContact : cardInfo.buyerName}
          </Text>
        </div>
        <div>
          {producerInternalJob
            ? cardInfo.billingCompany
            : cardInfo.buyerCompany}
        </div>
      </>
    );
    if (hasProducer && !isInternalJob) {
      n = (
        <div>
          <Text className="workspace-item-content-row-t">Revshop</Text>
        </div>
      );
    }
    return (
      <div className="role-info-block">
        <TextHeavy className="workspace-item-content-row-title">
          Buyer:
        </TextHeavy>
        {n}
      </div>
    );
  };

  const customerInfoNode = () => {
    const customerOrg = data.jobCustomerOrgInfoDTO;
    if (!customerOrg) return null;

    const address = [
      customerOrg.addressLine1,
      customerOrg.addressLine2,
      customerOrg.city,
      customerOrg.state,
      customerOrg.postalCode,
      customerOrg.country,
    ]
      .filter(Boolean)
      .join(', ');

    return (
      <div className="role-info-block border">
        <TextHeavy className="workspace-item-content-row-title">
          Customer:
        </TextHeavy>
        {customerOrg.name && (
          <div>
            <Text type="secondary">{customerOrg.name} - </Text>
          </div>
        )}
        {address && (
          <div>
            <Text type="secondary">{address}</Text>
          </div>
        )}
      </div>
    );
  };

  const shouldShowCustomerInfo =
    org?.orgType === OrgTypeEnum.SAAS &&
    (user?.siteRole === SiteRoleEnum.Sourcer ||
      user?.siteRole === SiteRoleEnum.Supplier);

  return (
    <>
      {/*{hasCancelStatus && <JobCancelHeader data={data} />}*/}
      <div
        className={`workspace-item ${activeTab ? 'workspace-item-active' : ''}`}
      >
        <RevxFlexLayout align="top">
          <div className="workspace-item-img">
            <RevxJobImg {...data} />
            <SectionHeading className="workspace-item-img-desc">
              {data.skuName}
            </SectionHeading>
            <Text type="secondary">
              {/* Named when it came from the ready-made list; see WorkspaceItemSize. */}
              <RevxJobSize
                isWorkspace
                size={data.size}
                sizeMetric={data.sizeMetric}
                sizePreset={data.sizePreset}
                sizePresetUnit={data.sizePresetUnit}
                orientation={data.orientation}
              />
            </Text>
            <CardInfoPDFs data={data} />
          </div>
          <div className="workspace-item-content">
            <div className="workspace-item-content-row">
              <div className="workspace-item-name-row-left">
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {isCustomerJob && (
                    <Tooltip title={jobOrgName}>
                      <ShopOutlined
                        style={{ color: '#52c41a', fontSize: '16px' }}
                      />
                    </Tooltip>
                  )}
                  <TextHeavy className="workspace-item-content-row-title">
                    {t('workspace.list.jobNo')}:
                  </TextHeavy>
                  <JobNum data={data} disabled={isJobDetail} />
                  <Divider type="vertical" />
                  <SectionHeading
                    className="workspace-item-name"
                    ellipsis={{
                      tooltip: data.name,
                    }}
                    editable={
                      data.customerId === user?.userId &&
                      !data.orderId && {
                        maxLength: JOB_NAME_MAX_LENGTH,
                        onChange: (value) => {
                          if (onUpdate) {
                            onUpdate({
                              ...data,
                              name: value.trim().replace(/\s+/g, ' '),
                            });
                          }
                        },
                        icon: <EditIcon />,
                      }
                    }
                  >
                    {data.name}
                  </SectionHeading>
                </div>
              </div>
            </div>

            <RevxFlexLayout
              className="workspace-item-content-row"
              justify="space-between"
            >
              <div>
                <TextHeavy className="workspace-item-content-row-title">
                  {t('workspace.list.quantity')}:
                </TextHeavy>
                <Text className="workspace-item-content-row-t">
                  {data.quantity}
                </Text>
                <Divider type="vertical" />
                {priceNode()}
              </div>
              {hasOperator &&
                data.workflowStatus === JobWorkflowStatusEnum.RFM && (
                  <div>
                    <span className="revx-job-status-label rfm">RFM</span>
                  </div>
                )}
            </RevxFlexLayout>

            <div className="workspace-item-content-row">
              <TextHeavy className="workspace-item-content-row-title">
                {t('workspace.list.shipDate')}:
              </TextHeavy>
              <Text className="workspace-item-content-row-t">
                {cardInfo.shipDate}
              </Text>
              {data.manualOverrideReadyDate && (
                <Tooltip
                  title={t('workspace.list.manualOverrideReadyDateTip')}
                  overlayStyle={{ whiteSpace: 'nowrap' }}
                >
                  <ClockCircleOutlined
                    style={{ color: '#faad14', marginLeft: 4 }}
                  />
                </Tooltip>
              )}
              <Divider type="vertical" />
              <TextHeavy className="workspace-item-content-row-title">
                {t('workspace.list.estimatedDeliveryDate')}:
              </TextHeavy>
              <Text className="workspace-item-content-row-t">
                {cardInfo.deliveryDate}
              </Text>
            </div>

            <RevxFlexLayout
              className={cs('workspace-item-content-row', {
                'hide-border': !showBillingContact,
              })}
              align="top"
            >
              {buyerInfoNode()}

              {shouldShowCustomerInfo && customerInfoNode()}

              {!hasProducer && (
                <div className="role-info-block border">
                  <TextHeavy className="workspace-item-content-row-title">
                    Supplier:
                  </TextHeavy>
                  {data.participants.length === 1 &&
                  (hasCustomer || hasProxy) ? (
                    <div>
                      <Text className="workspace-item-content-row-t">
                        {isInternalJob ? cardInfo.supplierOrgName : 'Revshop'}
                      </Text>
                    </div>
                  ) : (
                    <>
                      <div>
                        <Text className="workspace-item-content-row-t">
                          {cardInfo.supplierOrgName}
                        </Text>
                      </div>
                      <div>
                        {data.jobContacts?.supplierContacts.map((contact) => (
                          <span className="mr-10">
                            <ContactLink userContact={contact} />
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </RevxFlexLayout>

            <CardInfoContacts
              {...data}
              onViewShippingBilling={() =>
                setActiveTab(FilterTabEnum.SHIPPING_BILLING)
              }
            />

            {!!data.jobContacts?.revshopContacts.length && (
              <RevxFlexLayout className="workspace-item-revshop-contacts">
                <TextHeavy className="workspace-item-content-row-title">
                  Revshop contacts:
                </TextHeavy>
                <div>
                  {data.jobContacts?.revshopContacts.map((contact) => (
                    <span className="mr-10">
                      <ContactLink userContact={contact} />
                    </span>
                  ))}
                </div>
              </RevxFlexLayout>
            )}

            <div
              className="workspace-item-content-row"
              style={{ borderBottom: 0 }}
            >
              <WorkspaceFilterTabs
                data={data}
                defaultActiveTab={activeTab}
                onChange={(tab) => {
                  setActiveTab(tab);
                }}
              />
            </div>
          </div>
          <CardActions
            data={data}
            setActiveTab={setActiveTab}
            scrollLoading={scrollLoading}
            scrollDetailTab={scrollDetailTab}
          />
        </RevxFlexLayout>
        <div ref={ref}>
          <WorkspaceItemDetailWrapper
            activeTab={activeTab}
            jobData={data}
            onClose={() => setActiveTab(null)}
          />
        </div>
      </div>
    </>
  );
};

export default UniversalWorkspaceItem;
